import React from 'react';
import styled from 'styled-components';

import StickyButton from '@/components/atom/buttons/StickyButton';
import ClickLogger from '@/components/common/ClickLogger';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import LoginModal from '@/components/molecules/LoginModal';
import ReportSelectModal from '@/components/molecules/Modal/SelectModal/ReportSelectModal';
import Overlay from '@/components/molecules/Overlay';
import StickyAppHeader from '@/components/molecules/StickyAppHeader';
import DoctorDetailSkeleton from '@/components/templates/doctor/detail/components/DoctorDetailSkeleton/DoctorDetailSkeleton';
import DoctorSummary from '@/components/templates/doctor/detail/components/DoctorSummary/DoctorSummary';
import {
  DoctorDetailTabType,
  DoctorDetailViewModel,
  LoadedDoctorDetailViewModel,
} from '@/components/templates/doctor/detail/useDoctorDetailViewModel';
import { DoctorDetailLog } from '@/constants/logs/doctor';
import DNSlideTab from '@/designComponents/organisms/DNSlideTab';
import useReviewReportModal from '@/hooks/review/useReviewReportModal';
import theme from '@/styles/theme';
import { CustomPageProps } from '@/types/app';

import { Button } from 'doctornow-design-system';
import DoctorIntroduction from './components/DoctorIntroduction/DoctorIntroduction';
import DoctorReview from './components/DoctorReview/DoctorReview';
import F2FGuideBottomSheet from './components/F2FGuideBottomSheet/F2FGuideBottomSheet';
import NonBenefitPrice from './components/NonBenefitPrice/NonBenefitPrice';
import { DoctorReviewsViewModel } from './useDoctorReviewsViewModel';

interface Props extends CustomPageProps {
  doctorDetailViewModel: DoctorDetailViewModel;
  doctorReviewsViewModel: DoctorReviewsViewModel;
}
const DoctorDetailTemplate = ({ doctorDetailViewModel, doctorReviewsViewModel, client }: Props) => {
  const { showReportModal, onCloseReportModal, reportItems } = useReviewReportModal();

  const renderDoctorInfoSections = () => {
    return doctorDetailViewModel.getTabs().map((tab) => {
      switch (tab.value) {
        case DoctorDetailTabType.NonBenefitPrice:
          return <NonBenefitPrice key={tab.value} doctorDetailViewModel={doctorDetailViewModel} />;
        case DoctorDetailTabType.DoctorProfile:
          return (
            <DoctorIntroduction
              key={tab.value}
              doctorDetailViewModel={doctorDetailViewModel as LoadedDoctorDetailViewModel}
            />
          );
        case DoctorDetailTabType.Review:
          return (
            <DoctorReview key={tab.value} isMobile={client.isMobile} doctorReviewsViewModel={doctorReviewsViewModel} />
          );
        default:
          return null;
      }
    });
  };

  if (doctorDetailViewModel.isDoctorDetailLoading || !doctorDetailViewModel.doctorDetail) {
    return (
      <TemplateLayout.Wrapper>
        <TemplateLayout.StickyHeader>
          <StickyAppHeader isAbsolute backgroundColor={'transparent'} />
        </TemplateLayout.StickyHeader>
        <TemplateLayout.Content>
          <DoctorDetailSkeleton />
        </TemplateLayout.Content>
      </TemplateLayout.Wrapper>
    );
  }

  return (
    <TemplateLayout.Wrapper>
      {client.isWebView && (
        <TemplateLayout.StickyHeader>
          <StickyAppHeader
            isAbsolute
            backgroundColor={doctorDetailViewModel.isHeaderTransparent ? 'transparent' : theme.color.white}
          />
        </TemplateLayout.StickyHeader>
      )}
      <TemplateLayout.Content>
        <Wrapper>
          {!client.isMobile && <PaddingTop />}
          <ContentWrapper>
            <DoctorSummary
              doctorDetailViewModel={doctorDetailViewModel as LoadedDoctorDetailViewModel}
              doctorReviewsViewModel={doctorReviewsViewModel}
            />
          </ContentWrapper>
          <TabsSection stickToTop={!client.isMobile}>
            <DNSlideTab value={doctorDetailViewModel.selectedTab}>
              {doctorDetailViewModel.getTabs().map((tab) => (
                <DNSlideTab.Item
                  key={tab.key}
                  label={tab.label}
                  isActive={tab.value === doctorDetailViewModel.selectedTab}
                  onClick={() => doctorDetailViewModel.onClickTab(tab)}
                />
              ))}
            </DNSlideTab>
          </TabsSection>
          {renderDoctorInfoSections()}
          <ReportSelectModal isVisible={showReportModal} selectList={reportItems} onClose={onCloseReportModal} />
        </Wrapper>
        <StickyButton>
          <ClickLogger
            params={{
              eventName: DoctorDetailLog.DOCTOR_DETAIL_CLICK_TREATMENT_REQUEST,
            }}
          >
            <Col>
              <Button
                size={'xl'}
                disabled={!doctorDetailViewModel.getIsBookingAvailable()}
                onClick={
                  client.isWebView
                    ? doctorDetailViewModel.requestTreatmentIfLoggedIn
                    : doctorDetailViewModel.openInstallBottomSheet
                }
              >
                {doctorDetailViewModel.getTreatmentRequestButtonText()}
              </Button>
              {doctorDetailViewModel.isNonF2FDiagnosis() && (
                <StickButtonSubTitle>진료가 끝나기 전에는 진료비가 결제되지 않아요</StickButtonSubTitle>
              )}
            </Col>
          </ClickLogger>
        </StickyButton>
      </TemplateLayout.Content>

      <F2FGuideBottomSheet
        isOpened={doctorDetailViewModel.isOpenedF2FGuideBottomSheet}
        onClose={doctorDetailViewModel.closeF2FGuideBottomSheet}
        onClickF2FGuideBottomSheet={doctorDetailViewModel.onClickF2FGuideBottomSheet}
      />

      {doctorDetailViewModel.showLoginModal && (
        <Overlay onClickOverlay={doctorDetailViewModel.closeLoginModal}>
          <LoginModal
            isWritePage={false}
            onClick={doctorDetailViewModel.closeLoginModal}
            redirectUrl=''
            trackInfo={{
              campaign: 'doctor_detail',
              content: 'doctor_detail_family_doctor_add_remove',
              buttonText: '단골 의사 추가/삭제',
            }}
          />
        </Overlay>
      )}
    </TemplateLayout.Wrapper>
  );
};

export default DoctorDetailTemplate;

const Wrapper = styled.div`
  min-height: calc(100vh - 60px);
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TabsSection = styled.section<{ stickToTop: boolean }>`
  position: sticky;
  z-index: 2;

  ${({ theme, stickToTop }) => `
    top: ${stickToTop ? 0 : theme.size.mobileWebHeader.height}px;
    background: ${theme.color.white};
  `};
`;

const PaddingTop = styled.div`
  padding-top: 40px;
`;

const Col = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StickButtonSubTitle = styled.span`
  padding-top: 12px;
  width: 100%;
  text-align: center;
  ${({ theme }) => `
    ${theme.typography.UI13M};
    color: ${theme.color.gray400};
  `};
`;
